import React, { useEffect, useRef } from "react";
import { pickColor, rgba } from "./internal/colorUtils";

export const key = "lava-lamp";

const DEFAULT_GLASS_TOP = "#1a1033";
const DEFAULT_GLASS_BOTTOM = "#0c0820";
const DEFAULT_LAVA_A = "#f97316";
const DEFAULT_LAVA_B = "#dc2626";

const BLOB_DENSITY = 0.000018;   // blobs per pixel² — small (lamp-like)
const MIN_BLOBS = 5;
const MAX_BLOBS = 14;
const RISE_SPEED_BASE = 0.35;    // px/frame upward velocity when "hot"

function rand(min, max) { return min + Math.random() * (max - min); }

export default function LavaLamp({ colors = [], style }) {
  const canvasRef = useRef(null);
  const rafRef = useRef(null);
  const blobsRef = useRef([]);
  const colorsRef = useRef([DEFAULT_GLASS_TOP, DEFAULT_GLASS_BOTTOM, DEFAULT_LAVA_A, DEFAULT_LAVA_B]);

  useEffect(() => {
    colorsRef.current = [
      pickColor(colors, 0, DEFAULT_GLASS_TOP),
      pickColor(colors, 1, DEFAULT_GLASS_BOTTOM),
      pickColor(colors, 2, DEFAULT_LAVA_A),
      pickColor(colors, 3, DEFAULT_LAVA_B),
    ];
  }, [colors]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return undefined;
    const ctx = canvas.getContext("2d");
    if (!ctx) return undefined;

    let width = 0;
    let height = 0;
    const dpr = typeof window !== "undefined" ? Math.min(window.devicePixelRatio || 1, 2) : 1;
    let t = 0;

    // A blob lives in a vertical "lane" (a fixed x with small wobble)
    // because real lava lamps have very vertical convection. Its
    // temperature rises while at the bottom, falls when at the top, and
    // its vertical velocity follows that temperature → it slowly cycles
    // up and down with an organic feel.
    const newBlob = () => ({
      x: rand(width * 0.08, width * 0.92),
      y: rand(0, height),
      r: rand(28, 64),
      // Sine sway amplitude/period for slight horizontal wobble around lane.
      laneX: 0,
      swayPhase: rand(0, Math.PI * 2),
      swayPeriod: rand(0.18, 0.45),
      swayAmp: rand(8, 22),
      // Temperature 0..1 (1 = hot/rising fast, 0 = cool/falling).
      temp: Math.random(),
      tempVel: rand(-0.0009, 0.0009),
      slot: Math.random() < 0.5 ? 1 : 2, // chooses between lava A / lava B
    });

    const seedBlobs = () => {
      const target = Math.round(width * height * BLOB_DENSITY);
      const count = Math.max(MIN_BLOBS, Math.min(MAX_BLOBS, target));
      const arr = new Array(count);
      for (let i = 0; i < count; i += 1) {
        arr[i] = newBlob();
      }
      blobsRef.current = arr;
    };

    const resize = () => {
      const parent = canvas.parentElement;
      if (!parent) return;
      // Use clientWidth/Height (layout dims) so an ancestor `transform: scale(...)`
      // (e.g. PreviewDeviceStage) doesn't shrink the canvas and pin it to top-left.
      width = Math.max(1, parent.clientWidth || parent.offsetWidth || 0);
      height = Math.max(1, parent.clientHeight || parent.offsetHeight || 0);
      canvas.width = Math.floor(width * dpr);
      canvas.height = Math.floor(height * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      seedBlobs();
    };

    const tick = () => {
      const [glassTop, glassBottom, lavaA, lavaB] = colorsRef.current;

      // Glass: vertical gradient — darker at the bottom like lava-lamp
      // base shadow, lighter at the top.
      const grad = ctx.createLinearGradient(0, 0, 0, height);
      grad.addColorStop(0, glassTop);
      grad.addColorStop(1, glassBottom);
      ctx.fillStyle = grad;
      ctx.fillRect(0, 0, width, height);

      const blobs = blobsRef.current;

      // Update temperatures/positions.
      for (let i = 0; i < blobs.length; i += 1) {
        const b = blobs[i];
        // Temperature drifts. Bias toward heating up at the bottom and
        // cooling at the top so the lamp keeps recycling.
        const heightT = b.y / height;     // 0 top, 1 bottom
        const heatBias = (heightT - 0.5) * 0.0006;
        b.temp += b.tempVel + heatBias;
        if (b.temp > 1) { b.temp = 1; b.tempVel = -Math.abs(b.tempVel); }
        if (b.temp < 0) { b.temp = 0; b.tempVel = Math.abs(b.tempVel); }

        // Velocity: hot → rising (negative dy), cold → falling (positive dy).
        const vy = (0.5 - b.temp) * 2 * RISE_SPEED_BASE;
        b.y += vy;

        // Wobble sideways within the lane.
        b.laneX = Math.sin(t * b.swayPeriod + b.swayPhase) * b.swayAmp;

        // Soft wrap top/bottom so blobs always reappear.
        if (b.y < -b.r * 1.5) b.y = height + b.r * 0.5;
        if (b.y > height + b.r * 1.5) b.y = -b.r * 0.5;
      }

      // Two-pass render for the metaball-ish merging look:
      //   1) Add big soft radial gradients in 'lighter' so overlapping
      //      blobs brighten rather than just overlap, blending the colors.
      //   2) Draw a thin highlight on each blob to keep the ovals readable.
      ctx.globalCompositeOperation = "lighter";
      for (let i = 0; i < blobs.length; i += 1) {
        const b = blobs[i];
        const cx = b.x + b.laneX;
        // Stretch the radial gradient vertically so the blob looks like a
        // teardrop when rising or pancaking when falling — the way real
        // lava lamp blobs do.
        const stretch = 1 + (b.temp - 0.5) * 0.5;
        const r = b.r * stretch;
        const color = b.slot === 1 ? lavaA : lavaB;

        const lg = ctx.createRadialGradient(cx, b.y, 0, cx, b.y, r * 1.6);
        lg.addColorStop(0, rgba(color, 0.95));
        lg.addColorStop(0.5, rgba(color, 0.55));
        lg.addColorStop(1, rgba(color, 0));
        ctx.fillStyle = lg;
        ctx.beginPath();
        ctx.ellipse(cx, b.y, r * 0.85, r, 0, 0, Math.PI * 2);
        ctx.fill();
      }

      // Tiny white-ish specular highlight in source-over → top-left of
      // each blob, scaled with size. Reads as glassy reflection.
      ctx.globalCompositeOperation = "source-over";
      for (let i = 0; i < blobs.length; i += 1) {
        const b = blobs[i];
        const cx = b.x + b.laneX;
        const hr = b.r * 0.18;
        const hx = cx - b.r * 0.25;
        const hy = b.y - b.r * 0.45;
        const sg = ctx.createRadialGradient(hx, hy, 0, hx, hy, hr);
        sg.addColorStop(0, "rgba(255, 255, 255, 0.35)");
        sg.addColorStop(1, "rgba(255, 255, 255, 0)");
        ctx.fillStyle = sg;
        ctx.beginPath();
        ctx.arc(hx, hy, hr, 0, Math.PI * 2);
        ctx.fill();
      }

      t += 0.016;
      rafRef.current = requestAnimationFrame(tick);
    };

    resize();
    rafRef.current = requestAnimationFrame(tick);

    let ro = null;
    if (typeof ResizeObserver !== "undefined" && canvas.parentElement) {
      ro = new ResizeObserver(resize);
      ro.observe(canvas.parentElement);
    } else if (typeof window !== "undefined") {
      window.addEventListener("resize", resize);
    }

    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      if (ro) {
        try { ro.disconnect(); } catch { /* noop */ }
      } else if (typeof window !== "undefined") {
        window.removeEventListener("resize", resize);
      }
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      aria-hidden="true"
      style={{
        position: "absolute",
        inset: 0,
        width: "100%",
        height: "100%",
        display: "block",
        pointerEvents: "none",
        ...style,
      }}
    />
  );
}
